/**
 * Shared building-blocks for single-record and batch import dialogs.
 *
 * Exports:
 *  - DIFF_STATES, DECISIONS, WRITE_POLICIES  — constants
 *  - pbValue, diffState, defaultDecision, prettyField, willWrite  — helpers
 *  - DiffTable          — per-field side-by-side diff table with per-row decision selects
 *  - VerdictBadge       — "New in CRM" / "Updates existing" pill
 *  - PersonRow          — collapsible person row with checkbox + DiffTable
 *  - ImportResultLinks  — post-import company + contacts link list
 */
import React, { useMemo, useState } from 'react';
import {
    ArrowRight, ChevronDown, ChevronRight, ExternalLink, Search, Building2,
} from 'lucide-react';
import { isEmpty, valuesEqual, fmtValue } from '../../utils/pitchbookDiff';

// ─── Constants ────────────────────────────────────────────────────────────────

export const DIFF_STATES = {
    conflict: { label: 'Differs',   cls: 'bg-amber-50 text-amber-700 border-amber-200',      order: 0 },
    fill:     { label: 'Will fill', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', order: 1 },
    match:    { label: 'Match',     cls: 'bg-stone-100 text-stone-500 border-stone-200',      order: 2 },
    empty:    { label: 'No data',   cls: 'bg-stone-50 text-stone-400 border-stone-200',       order: 3 },
};

export const DECISIONS = [
    { value: 'fill_blank', label: 'Fill if blank' },
    { value: 'always',     label: 'Overwrite' },
    { value: 'ignore',     label: 'Skip' },
];

export const WRITE_POLICIES = ['fill_blank', 'always', 'ignore'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** The value as it would actually be written to the CRM (backend-converted). */
export function pbValue(row) {
    return row.crm_value !== undefined && row.crm_value !== null
        ? row.crm_value : row.coerced_value;
}

export function diffState(row) {
    if (isEmpty(pbValue(row))) return 'empty';
    if (isEmpty(row.crm_current)) return 'fill';
    if (valuesEqual(pbValue(row), row.crm_current, row.target)) return 'match';
    return 'conflict';
}

export function defaultDecision(row) {
    return WRITE_POLICIES.includes(row.policy) ? row.policy : 'ignore';
}

export function prettyField(f) {
    if (!f) return '';
    return f.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()).replace(/\bUrl\b/, 'URL');
}

/** Whether a row will be written given the chosen decision. */
export function willWrite(row, decision) {
    if (isEmpty(pbValue(row))) return false;
    if (decision === 'always') return true;
    if (decision === 'fill_blank') return isEmpty(row.crm_current);
    return false;
}

// ─── VerdictBadge ─────────────────────────────────────────────────────────────

export function VerdictBadge({ verdict }) {
    if (!verdict || verdict === 'skip_no_change') {
        return (
            <span className="px-2 py-0.5 rounded text-[10px] font-medium border bg-stone-50 text-stone-500 border-stone-200 shrink-0">
                No change
            </span>
        );
    }
    if (verdict === 'ambiguous') {
        return (
            <span className="px-2 py-0.5 rounded text-[10px] font-medium border bg-amber-50 text-amber-700 border-amber-200 shrink-0">
                Needs review
            </span>
        );
    }
    const isCreate = verdict === 'create';
    return (
        <span className={`px-2 py-0.5 rounded text-[10px] font-medium border shrink-0 ${
            isCreate
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
            {isCreate ? 'New in CRM' : 'Updates existing'}
        </span>
    );
}

// ─── DiffTable ────────────────────────────────────────────────────────────────

export function DiffTable({ fields, decisions, onDecision, isUpdate }) {
    const [showUnmapped, setShowUnmapped] = useState(false);

    const { mapped, unmapped } = useMemo(() => {
        const m = [];
        const u = [];
        for (const f of fields) {
            if (!f.target || f.policy === 'pitchbook_tab_only') u.push(f);
            else m.push(f);
        }
        m.sort((a, b) => DIFF_STATES[diffState(a)].order - DIFF_STATES[diffState(b)].order
            || a.csv_column.localeCompare(b.csv_column));
        return { mapped: m, unmapped: u };
    }, [fields]);

    return (
        <div className="border border-stone-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs table-fixed">
                <thead className="bg-stone-50 text-stone-500">
                    <tr>
                        <th className="text-left px-3 py-2 font-medium w-[22%]">Field</th>
                        <th className="text-left px-3 py-2 font-medium w-[26%]">PitchBook value</th>
                        <th className="w-6" />
                        <th className="text-left px-3 py-2 font-medium w-[26%]">CRM current</th>
                        <th className="text-left px-3 py-2 font-medium w-[10%]">Status</th>
                        <th className="text-left px-3 py-2 font-medium w-[16%]">Decision</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                    {mapped.map((f) => {
                        const state = diffState(f);
                        const meta = DIFF_STATES[state];
                        const decision = decisions[f.csv_column] ?? defaultDecision(f);
                        const pb = fmtValue(pbValue(f));
                        const crm = fmtValue(f.crm_current);
                        const writes = willWrite(f, decision);
                        return (
                            <tr key={f.csv_column} className={`hover:bg-stone-50 ${state === 'empty' ? 'opacity-60' : ''}`}>
                                <td className="px-3 py-1.5 align-top">
                                    <span className="text-stone-700 font-medium block truncate" title={f.target}>{prettyField(f.target)}</span>
                                    <code className="text-[10px] text-stone-400 block truncate" title={f.csv_column}>{f.csv_column}</code>
                                </td>
                                <td className={`px-3 py-1.5 align-top break-words ${writes ? 'text-stone-800' : 'text-stone-500'}`}
                                    title={f.raw_value && f.raw_value !== pb ? `PitchBook: ${f.raw_value}` : undefined}>
                                    {pb ?? <span className="text-stone-300">—</span>}
                                </td>
                                <td className="text-center align-top py-1.5">
                                    <ArrowRight className={`w-3 h-3 inline ${writes ? 'text-emerald-500' : 'text-stone-200'}`} />
                                </td>
                                <td className="px-3 py-1.5 align-top break-words text-stone-500">
                                    {crm ?? <span className="text-stone-300">{isUpdate ? '—' : 'new record'}</span>}
                                </td>
                                <td className="px-3 py-1.5 align-top">
                                    <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-medium whitespace-nowrap ${meta.cls}`}>
                                        {meta.label}
                                    </span>
                                </td>
                                <td className="px-3 py-1.5 align-top">
                                    {state === 'empty' ? (
                                        <span className="text-stone-300 text-[10px]">—</span>
                                    ) : (
                                        <select
                                            value={decision}
                                            onChange={(e) => onDecision(f.csv_column, e.target.value)}
                                            className={`w-full h-7 text-[11px] rounded-md border px-1.5 bg-white outline-none focus:border-stone-400
                                                ${decision !== defaultDecision(f) ? 'border-amber-300 text-amber-700' : 'border-stone-200 text-stone-600'}`}
                                        >
                                            {DECISIONS.map((d) => (
                                                <option key={d.value} value={d.value}>{d.label}</option>
                                            ))}
                                        </select>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {unmapped.length > 0 && (
                <button
                    onClick={() => setShowUnmapped((v) => !v)}
                    className="w-full flex items-center gap-1.5 px-3 py-2 text-[11px] text-stone-400 hover:text-stone-600 bg-stone-50/50 border-t border-stone-100"
                >
                    {showUnmapped ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    {unmapped.length} column{unmapped.length === 1 ? '' : 's'} not mapped to the CRM (kept on the PitchBook tab only)
                </button>
            )}
            {showUnmapped && (
                <div className="px-3 py-2 border-t border-stone-100 grid grid-cols-2 gap-x-6 gap-y-0.5">
                    {unmapped.map((f) => (
                        <div key={f.csv_column} className="flex items-baseline gap-2 text-[11px] min-w-0">
                            <code className="text-stone-500 shrink-0">{f.csv_column}</code>
                            <span className="text-stone-400 truncate">{f.raw_value || '—'}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── PersonRow ────────────────────────────────────────────────────────────────

export function PersonRow({ person, selected, expanded, onToggleSelect, onToggleExpand, decisions, onDecision, highlighted }) {
    return (
        <div className={`border rounded-lg ${highlighted ? 'border-stone-400 ring-1 ring-stone-200' : 'border-stone-200'}`}>
            <div className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-stone-50">
                <label className="flex items-center gap-2 min-w-0 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!selected}
                        onChange={(e) => onToggleSelect(e.target.checked)}
                        className="rounded border-stone-300 text-stone-900 focus:ring-stone-700"
                    />
                    <button type="button"
                        onClick={onToggleExpand}
                        className="flex items-center gap-1.5 text-sm text-stone-700 truncate text-left">
                        {expanded
                            ? <ChevronDown className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                            : <ChevronRight className="w-3.5 h-3.5 text-stone-400 shrink-0" />}
                        <span className="truncate">{person.name || person.pitchbook_id}</span>
                    </button>
                </label>
                <VerdictBadge verdict={person.verdict} />
            </div>
            {expanded && (
                <div className="p-2 border-t border-stone-100">
                    <DiffTable
                        fields={person.fields}
                        decisions={decisions}
                        onDecision={onDecision}
                        isUpdate={person.verdict !== 'create'}
                    />
                </div>
            )}
        </div>
    );
}

// ─── ImportResultLinks ────────────────────────────────────────────────────────

const CONTACT_SEARCH_THRESHOLD = 10;

export function ImportResultLinks({ links }) {
    const [query, setQuery] = useState('');
    const accounts = useMemo(() => (links || []).filter((l) => l.type === 'account'), [links]);
    const contacts = useMemo(() => (links || []).filter((l) => l.type === 'contact'), [links]);
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return contacts;
        return contacts.filter((l) =>
            (l.name || '').toLowerCase().includes(q) || (l.title || '').toLowerCase().includes(q));
    }, [contacts, query]);

    if (!accounts.length && !contacts.length) return null;

    return (
        <div className="space-y-3 pt-2">
            {accounts.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2">
                    {accounts.map((l) => (
                        <a key={l.id} href={`/companies/${l.id}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-stone-900 text-white hover:bg-stone-700 font-medium">
                            <Building2 className="w-4 h-4" />
                            Open {l.name || 'company'}
                            <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                        </a>
                    ))}
                </div>
            )}

            {contacts.length > 0 && (
                <div className="max-w-2xl mx-auto text-left border border-stone-200 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between gap-3 px-3 py-2 bg-stone-50 border-b border-stone-200">
                        <span className="text-xs font-medium text-stone-600">
                            {contacts.length} contact{contacts.length === 1 ? '' : 's'} imported
                            {query && filtered.length !== contacts.length && ` · ${filtered.length} shown`}
                        </span>
                        {contacts.length > CONTACT_SEARCH_THRESHOLD && (
                            <div className="relative">
                                <Search className="w-3.5 h-3.5 text-stone-400 absolute left-2 top-1/2 -translate-y-1/2" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Find a contact…"
                                    className="pl-7 pr-2 py-1 text-xs border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-stone-400 w-48"
                                />
                            </div>
                        )}
                    </div>
                    <div className="max-h-60 overflow-y-auto divide-y divide-stone-100">
                        {filtered.map((l) => (
                            <a key={l.id} href={`/contacts/${l.id}`} target="_blank" rel="noreferrer"
                                className="flex items-center justify-between gap-3 px-3 py-1.5 hover:bg-stone-50 group">
                                <span className="min-w-0 flex items-baseline gap-2">
                                    <span className="text-sm text-stone-800 truncate">{l.name || 'Contact'}</span>
                                    {l.title && <span className="text-xs text-stone-400 truncate">{l.title}</span>}
                                </span>
                                <span className="text-xs text-stone-400 group-hover:text-stone-700 shrink-0 inline-flex items-center gap-1">
                                    Open <ExternalLink className="w-3 h-3" />
                                </span>
                            </a>
                        ))}
                        {filtered.length === 0 && (
                            <div className="px-3 py-4 text-center text-xs text-stone-400">No contacts match "{query}"</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
